/**
 * VerificationHistoryPanel
 * ----------------------------------------------------------------------------
 * Slide-out panel showing the append-only Verification Log timeline for a
 * single requirement. Built self-contained (own styled-components, no external
 * CSS dependency) so it can be mounted from anywhere — the Requirements
 * Register today, and the Compliance Tracker detail panel (Zone 3) later.
 *
 * Pixel-matched to src/CODiii_RequirementsRegister_VerificationLog_Prototype.html
 *
 * Decoupling contract: this component imports NOTHING from
 * ExtractedRequirementsPage. It renders from `requirementId` plus an optional
 * `requirement` object the caller already has on hand.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const API_BASE = process.env.REACT_APP_API_BASE_VERIFICATION_LOG;

function getAuthHeaders() {
  const idToken = localStorage.getItem('id_token');
  return idToken ? { Authorization: `Bearer ${idToken}` } : {};
}

/* ===================================================================
   Labels / formatters (ported from prototype)
   =================================================================== */
const VS_LABELS = {
  verified: 'Verified',
  non_compliant: 'Non-Compliant',
  needs_review: 'Needs Review',
  not_applicable: 'Not Applicable',
  stale: 'Stale',
  unverified: 'Unverified',
};
const EVT_LABELS = {
  scan: 'Scan',
  manual_override: 'Manual Override',
  issue_resolved: 'Issue Resolved',
  stale: 'Re-extracted',
};
const VD_LABELS = {
  compliant: 'Verified',
  non_compliant: 'Non-Compliant',
  needs_review: 'Needs Review',
  not_applicable: 'Not Applicable',
  stale: 'Stale',
};
const CT_LABELS = {
  BUILDING_CODE: 'Building Code',
  CLIENT_REQUIREMENTS: 'Client Req.',
  ZONING: 'Zoning',
  STANDARDS: 'Standards',
};
// Human-readable manual-override types
const OVERRIDE_TYPE_LABELS = {
  mark_compliant: 'Marked Compliant',
  mark_non_compliant: 'Marked Non-Compliant',
  mark_not_applicable: 'Marked Not Applicable',
  mark_needs_review: 'Flagged for Review',
};

function fmtDate(iso) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}
function fmtRel(iso) {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return m + 'm ago';
  const h = Math.round(m / 60);
  if (h < 24) return h + 'h ago';
  const d = Math.round(h / 24);
  if (d < 30) return d + 'd ago';
  return Math.round(d / 30) + 'mo ago';
}

// Map an event_type to its timeline class + dot colors
function eventVisuals(et) {
  switch (et) {
    case 'scan':
      return { cls: 'scan', dotBg: 'rgba(233,112,36,0.12)', dotFg: '#e97024' };
    case 'manual_override':
      return { cls: 'override', dotBg: 'rgba(59,130,246,0.12)', dotFg: '#2563eb' };
    case 'issue_resolved':
      return { cls: 'resolved', dotBg: 'rgba(6,182,212,0.12)', dotFg: '#0891b2' };
    case 'stale':
    default:
      return { cls: 'stale', dotBg: '#f0f0f0', dotFg: '#666' };
  }
}

const ICONS = {
  scan: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
    </svg>
  ),
  stale: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" />
    </svg>
  ),
  override: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  ),
  resolved: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  ),
};

const DocIcon = () => (
  <svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

/* ===================================================================
   Data layer (NOT YET WIRED)
   -------------------------------------------------------------------
   TODO(Shivani): no GraphQL query / FastAPI endpoint exists yet to read
   VerificationLog by PK REQUIREMENT#{requirementId} (SK
   EVENT#{verified_at}#{log_id} DESC) — see plan "Data layer". Until that
   lands this must NOT return fabricated data; doing so would render fake
   audit-trail entries as if they were real. Returns empty + a flag the UI
   uses to show "not available yet" instead of a misleading "no events"
   empty state. Swap this one function for the real call once the API ships.
   =================================================================== */
const VERIFICATION_LOG_API_READY = true;

async function fetchVerificationLog(requirementId, projectId) {
  const params = new URLSearchParams({ requirementId, projectId });
  const res = await fetch(`${API_BASE}/verification-log?${params}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.events || [];
}

/* ===================================================================
   Styled components (ported from prototype)
   =================================================================== */
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.25);
  z-index: 900;
  opacity: ${p => (p.$open ? 1 : 0)};
  pointer-events: ${p => (p.$open ? 'auto' : 'none')};
  transition: opacity 0.25s ease;
`;

const Panel = styled.div`
  position: fixed; top: 0; right: ${p => p.$offsetRight || 0}px; bottom: 0;
  width: 520px; max-width: 90vw;
  background: #ffffff;
  box-shadow: -4px 0 24px rgba(0,0,0,0.12);
  z-index: 1000;
  transform: translateX(${p => (p.$open ? '0' : '100%')});
  transition: transform 0.3s ease;
  display: flex; flex-direction: column; overflow: hidden;
  font-family: 'Poppins', sans-serif;
  color: #333333;
  @media (max-width: 900px) { width: 100%; max-width: 100%; right: 0; }
`;

const Header = styled.div`
  padding: 1.25rem 1.5rem; border-bottom: 1px solid #f0f0f0;
  display: flex; align-items: flex-start; gap: 1rem; flex-shrink: 0;
`;
const CloseBtn = styled.button`
  flex: none; width: 32px; height: 32px; border-radius: 8px;
  border: 1px solid #f0f0f0; background: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: #666; transition: all 0.15s;
  &:hover { background: #f5f5f5; border-color: #e0e0e0; color: #333; }
  svg { width: 16px; height: 16px; }
`;
const HeaderInfo = styled.div` flex: 1; min-width: 0; `;
const HeaderTitle = styled.div` font-size: 1rem; font-weight: 600; line-height: 1.4; color: #333; `;
const HeaderBadges = styled.div` display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.5rem; `;

const Body = styled.div`
  flex: 1; overflow-y: auto; padding: 1.25rem 1.5rem 2rem;
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 3px; }
`;

const Badge = styled.span`
  display: inline-flex; align-items: center;
  padding: 0.2rem 0.55rem; border-radius: 6px;
  font-size: 0.72rem; font-weight: 500; letter-spacing: 0.3px; white-space: nowrap;
  background: ${p =>
    p.$type === 'BUILDING_CODE' ? 'rgba(139,92,246,0.1)' :
    p.$type === 'CLIENT_REQUIREMENTS' ? 'rgba(233,112,36,0.1)' :
    p.$type === 'ZONING' ? 'rgba(6,182,212,0.1)' :
    p.$type === 'STANDARDS' ? 'rgba(59,130,246,0.1)' : 'rgba(0,0,0,0.06)'};
  color: ${p =>
    p.$type === 'BUILDING_CODE' ? '#7c3aed' :
    p.$type === 'CLIENT_REQUIREMENTS' ? '#e97024' :
    p.$type === 'ZONING' ? '#0891b2' :
    p.$type === 'STANDARDS' ? '#2563eb' : '#666666'};
`;
const SubBadge = styled.span`
  display: inline-flex; align-items: center;
  padding: 0.2rem 0.55rem; border-radius: 6px;
  font-size: 0.68rem; letter-spacing: 0.3px; text-transform: uppercase; white-space: nowrap;
  background: #fafafa; border: 1px solid #e0e0e0; color: #666;
`;

const VS_STYLES = {
  verified: 'background:rgba(76,175,80,0.15);color:#388e3c;',
  non_compliant: 'background:rgba(244,67,54,0.15);color:#d32f2f;',
  needs_review: 'background:rgba(255,193,7,0.15);color:#f57c00;',
  not_applicable: 'background:#f0f0f0;color:#666666;',
  stale: 'background:rgba(139,92,246,0.1);color:#7c3aed;',
  unverified: 'background:#fafafa;color:#999999;border:1px solid #e0e0e0;',
};
const VsBadge = styled.span`
  display: inline-flex; align-items: center; gap: 0.25rem;
  padding: 0.18rem 0.5rem; border-radius: 999px;
  font-size: 0.68rem; font-weight: 600; white-space: nowrap; line-height: 1.5;
  ${p => VS_STYLES[p.$status] || VS_STYLES.unverified}
`;

const SectionTitle = styled.div`
  font-size: 0.72rem; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.5px; color: #999;
  margin-bottom: 0.75rem; padding-bottom: 0.5rem; border-bottom: 1px solid #f0f0f0;
  display: flex; align-items: center; gap: 0.4rem;
  svg { width: 13px; height: 13px; }
`;
const SourceDocBlock = styled.div` margin-bottom: 1.25rem; `;
const SourceDocRow = styled.div` margin-top: 0.5rem; `;
const EventCount = styled.span`
  display: inline-flex; align-items: center;
  background: #f0f0f0; border-radius: 999px;
  padding: 0.1rem 0.5rem; font-size: 0.68rem; font-weight: 700; color: #666;
`;

const DocPill = styled.span`
  display: inline-flex; align-items: center; gap: 0.3rem;
  background: #eef2ff; border: 1px solid #c7d2fe; border-radius: 999px;
  padding: 0.15rem 0.5rem; font-size: 0.72rem; color: #312e81;
  max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  svg { flex-shrink: 0; color: #6366f1; }
  ${p => p.$clickable && `
    cursor: pointer;
    &:hover { background: #e0e7ff; border-color: #a5b4fc; }
  `}
`;
const DocPillPage = styled.span` font-weight: 700; margin-left: 2px; color: #4f46e5; `;

/* timeline */
const TimelineWrap = styled.div`
  position: relative; padding-left: 1.6rem;
  &::before {
    content: ''; position: absolute; left: 7px; top: 6px; bottom: 6px;
    width: 2px; background: #e8e8e8;
  }
`;
const Event = styled.div`
  position: relative; padding-bottom: 1.25rem;
  &:last-child { padding-bottom: 0; }
`;
const Dot = styled.div`
  position: absolute; left: -1.6rem; top: 0.15rem;
  width: 16px; height: 16px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid #fff; box-shadow: 0 0 0 1px #e0e0e0;
  background: ${p => p.$bg}; color: ${p => p.$fg};
  svg { width: 9px; height: 9px; }
`;
const EvtHead = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 0.4rem; margin-bottom: 0.35rem;
`;
const EVT_STYLES = {
  scan: 'background:rgba(233,112,36,0.1);color:#e97024;',
  override: 'background:rgba(59,130,246,0.1);color:#2563eb;',
  resolved: 'background:rgba(6,182,212,0.1);color:#0891b2;',
  stale: 'background:#f0f0f0;color:#666666;',
};
const EvtBadge = styled.span`
  display: inline-flex; align-items: center; gap: 0.25rem;
  font-size: 0.7rem; font-weight: 600; padding: 0.18rem 0.5rem; border-radius: 999px;
  ${p => EVT_STYLES[p.$cls] || EVT_STYLES.stale}
  svg { width: 10px; height: 10px; }
`;
const VD_STYLES = {
  compliant: 'background:rgba(76,175,80,0.15);color:#388e3c;',
  non_compliant: 'background:rgba(244,67,54,0.15);color:#d32f2f;',
  needs_review: 'background:rgba(255,193,7,0.15);color:#f57c00;',
  not_applicable: 'background:#f0f0f0;color:#666666;',
  stale: 'background:rgba(139,92,246,0.1);color:#7c3aed;',
};
const VdBadge = styled.span`
  display: inline-flex; align-items: center;
  font-size: 0.7rem; font-weight: 600; padding: 0.18rem 0.5rem; border-radius: 999px;
  ${p => VD_STYLES[p.$verdict] || ''}
`;
const Time = styled.span`
  font-size: 0.72rem; color: #999;
  .rel { color: #666; font-weight: 500; }
`;
const DetailCard = styled.div`
  background: #fafafa; border: 1px solid #f0f0f0; border-radius: 8px;
  padding: 0.6rem 0.75rem; font-size: 0.78rem; color: #666; line-height: 1.55;
`;
const DetailRow = styled.div`
  display: flex; gap: 0.4rem; margin-bottom: 0.2rem;
  &:last-child { margin-bottom: 0; }
`;
const DetailLabel = styled.span`
  flex: none; width: 90px; color: #999; font-weight: 600;
  font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.3px; padding-top: 0.1rem;
`;
const DetailValue = styled.span`
  flex: 1; min-width: 0;
  ${p => p.$quote && 'color:#333; font-style:italic;'}
  ${p => p.$clickable && `
    color: #4f46e5; cursor: pointer; text-decoration: underline;
    &:hover { color: #3730a3; }
  `}
`;
const Empty = styled.div`
  text-align: center; padding: 2rem 1rem; color: #999; font-size: 0.82rem;
`;
const Loading = styled(Empty)``;

/* ===================================================================
   Component
   =================================================================== */
export default function VerificationHistoryPanel({
  requirementId,
  requirement = null,
  projectId,
  isOpen = false,
  onClose,
  // 'overlay' (default, Register usage): dimmed full-viewport backdrop, click-to-close.
  // 'docked' (Compliance detail panel usage): no backdrop, sits beside another open
  // panel so both remain independently interactive at once.
  variant = 'overlay',
  // Pixels to offset from the right edge — lets this dock to the left of another
  // fixed-position panel (e.g. the 400px-wide Compliance issue detail panel).
  offsetRight = 0,
  // Optional callback to open the requirement's source document (in the shared
  // PDF viewer) at its recorded page. Caller owns auth/PDF-resolution — this
  // component stays decoupled and just forwards the `requirement` it was given.
  onOpenSourceDoc,
  // Optional callback to open a scan event's deliverable document at the page
  // the evidence was found on. Called with the raw event object.
  onOpenDeliverable,
}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const closeRef = useRef(null);
  const prevFocusRef = useRef(null);

  // Escape to close + focus management
  useEffect(() => {
    if (!isOpen) return undefined;
    prevFocusRef.current = document.activeElement;
    const t = setTimeout(() => closeRef.current?.focus(), 50);
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      // restore focus to the trigger
      if (prevFocusRef.current && prevFocusRef.current.focus) {
        prevFocusRef.current.focus();
      }
    };
  }, [isOpen, onClose]);

  // Fetch the log whenever an open panel points at a (new) requirement
  useEffect(() => {
    if (!isOpen || !requirementId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEvents([]);
    fetchVerificationLog(requirementId, projectId)
      .then((rows) => { if (!cancelled) setEvents(rows || []); })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, requirementId, projectId]);

  const r = requirement || {};
  const status = r.verificationStatus || r.verification_status || 'unverified';
  const docName = r.sourceDocName || '';
  const docShort = docName.length > 22 ? docName.slice(0, 22) + '…' : docName;

  const sorted = [...events].sort(
    (a, b) => new Date(b.verified_at) - new Date(a.verified_at)
  );

  // Portal to <body> so this fixed-position panel always paints/positions
  // against the real viewport — never clipped or mis-offset by whatever
  // flex/overflow ancestor (e.g. the Compliance issue detail panel's aside)
  // happens to mount it.
  return createPortal(
    <>
      {variant === 'overlay' && <Overlay $open={isOpen} onClick={onClose} />}
      <Panel
        $open={isOpen}
        $offsetRight={offsetRight}
        role="dialog"
        aria-modal="true"
        aria-label="Verification history"
        aria-hidden={!isOpen}
      >
        <Header>
          <CloseBtn ref={closeRef} onClick={onClose} title="Close" aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </CloseBtn>
          <HeaderInfo>
            <HeaderTitle>{r.requirementText || 'Requirement'}</HeaderTitle>
            <HeaderBadges>
              {r.complianceType && (
                <Badge $type={r.complianceType}>
                  {CT_LABELS[r.complianceType] || r.complianceType}
                </Badge>
              )}
              {(r.subcategory || r.compliance_subcategory) && (
                <SubBadge>{r.subcategory || r.compliance_subcategory}</SubBadge>
              )}
              <VsBadge $status={status}>{VS_LABELS[status] || status}</VsBadge>
            </HeaderBadges>
          </HeaderInfo>
        </Header>

        <Body>
          {/* meta: source document */}
          {docName && (
            <SourceDocBlock>
              <SectionTitle>Requirement Source Document</SectionTitle>
              <SourceDocRow>
                <DocPill
                  title={onOpenSourceDoc ? `Open ${docName}` : docName}
                  $clickable={!!onOpenSourceDoc}
                  role={onOpenSourceDoc ? 'button' : undefined}
                  tabIndex={onOpenSourceDoc ? 0 : undefined}
                  onClick={onOpenSourceDoc ? () => onOpenSourceDoc(r) : undefined}
                  onKeyDown={onOpenSourceDoc ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenSourceDoc(r); }
                  } : undefined}
                >
                  <DocIcon />
                  {docShort}
                  {r.pageNumber > 0 && <DocPillPage>p. {r.pageNumber}</DocPillPage>}
                </DocPill>
              </SourceDocRow>
            </SourceDocBlock>
          )}

          <SectionTitle>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Verification History
            {!loading && !error && <EventCount>{events.length}</EventCount>}
          </SectionTitle>

          {loading && <Loading>Loading verification history…</Loading>}
          {error && !loading && (
            <Empty>Couldn’t load verification history. Please try again.</Empty>
          )}

          {!loading && !error && events.length === 0 && !VERIFICATION_LOG_API_READY && (
            <Empty>
              Verification history isn't wired up yet — the read API for this
              view hasn't shipped. This is a known gap, not a sign there's no
              history.
            </Empty>
          )}

          {!loading && !error && events.length === 0 && VERIFICATION_LOG_API_READY && (
            <Empty>
              No verification events recorded yet.<br />
              Run a compliance check scan to generate the first entry.
            </Empty>
          )}

          {!loading && !error && events.length > 0 && (
            <TimelineWrap>
              {sorted.map((e) => {
                const v = eventVisuals(e.event_type);
                const icon = ICONS[v.cls] || ICONS.stale;
                // deliverable_id is an internal doc hash, not something a user can
                // act on — only show the human-readable name once the backend sends
                // one; otherwise omit the row rather than leak the raw id.
                const deliverable = e.deliverable_name || null;
                return (
                  <Event key={e.log_id}>
                    <Dot $bg={v.dotBg} $fg={v.dotFg}>{icon}</Dot>
                    <EvtHead>
                      <EvtBadge $cls={v.cls}>
                        {icon}
                        {EVT_LABELS[e.event_type] || e.event_type}
                      </EvtBadge>
                      {e.verdict && (
                        <VdBadge $verdict={e.verdict}>
                          {VD_LABELS[e.verdict] || e.verdict}
                        </VdBadge>
                      )}
                      <Time>
                        <span className="rel">{fmtRel(e.verified_at)}</span> · {fmtDate(e.verified_at)}
                      </Time>
                    </EvtHead>

                    {e.event_type === 'scan' && (
                      <DetailCard>
                        {deliverable && (
                          <DetailRow>
                            <DetailLabel>Deliverable</DetailLabel>
                            <DetailValue
                              $clickable={!!onOpenDeliverable}
                              role={onOpenDeliverable ? 'button' : undefined}
                              tabIndex={onOpenDeliverable ? 0 : undefined}
                              onClick={onOpenDeliverable ? () => onOpenDeliverable(e) : undefined}
                              onKeyDown={onOpenDeliverable ? (ev) => {
                                if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onOpenDeliverable(e); }
                              } : undefined}
                            >
                              {deliverable}
                            </DetailValue>
                          </DetailRow>
                        )}
                        {e.evidence_location && (
                          <DetailRow>
                            <DetailLabel>Location</DetailLabel>
                            <DetailValue>{e.evidence_location}</DetailValue>
                          </DetailRow>
                        )}
                        {e.evidence_statement && (
                          <DetailRow>
                            <DetailLabel>Evidence</DetailLabel>
                            <DetailValue $quote>“{e.evidence_statement}”</DetailValue>
                          </DetailRow>
                        )}
                      </DetailCard>
                    )}

                    {e.event_type === 'manual_override' && (
                      <DetailCard>
                        <DetailRow>
                          <DetailLabel>Override</DetailLabel>
                          <DetailValue>
                            {OVERRIDE_TYPE_LABELS[e.override_type] || e.override_type || '—'}
                          </DetailValue>
                        </DetailRow>
                        {e.reason && (
                          <DetailRow>
                            <DetailLabel>Reason</DetailLabel>
                            <DetailValue $quote>“{e.reason}”</DetailValue>
                          </DetailRow>
                        )}
                        {e.user && (
                          <DetailRow>
                            <DetailLabel>User</DetailLabel>
                            <DetailValue>{e.user}</DetailValue>
                          </DetailRow>
                        )}
                      </DetailCard>
                    )}

                    {e.event_type === 'issue_resolved' && (
                      <DetailCard>
                        {e.issue_id && (
                          <DetailRow>
                            <DetailLabel>Issue</DetailLabel>
                            <DetailValue>{e.issue_id}</DetailValue>
                          </DetailRow>
                        )}
                        {e.user && (
                          <DetailRow>
                            <DetailLabel>User</DetailLabel>
                            <DetailValue>{e.user}</DetailValue>
                          </DetailRow>
                        )}
                        <DetailRow>
                          <DetailLabel>Note</DetailLabel>
                          <DetailValue>
                            Verification status is unchanged until a new scan confirms it.
                          </DetailValue>
                        </DetailRow>
                      </DetailCard>
                    )}

                    {e.event_type === 'stale' && (
                      <DetailCard>
                        Source document was re-extracted. This requirement needs a new
                        compliance check scan to restore verified status.
                      </DetailCard>
                    )}
                  </Event>
                );
              })}
            </TimelineWrap>
          )}
        </Body>
      </Panel>
    </>,
    document.body
  );
}
